<template>
  <div class="min-h-screen bg-[#111111] relative overflow-hidden">
    <!-- Grid Overlays -->
    <div class="absolute inset-0 pointer-events-none z-0">
      <!-- Vertical lines -->
      <div class="absolute left-[10%] top-0 bottom-0 w-px bg-[#2C2C2C] opacity-20"></div>
      <div class="absolute left-[15%] top-0 bottom-0 w-px bg-[#2C2C2C] opacity-10"></div>
      <div class="absolute right-[10%] top-0 bottom-0 w-px bg-[#2C2C2C] opacity-20"></div>
      <div class="absolute right-[15%] top-0 bottom-0 w-px bg-[#2C2C2C] opacity-10"></div>
      <!-- Horizontal lines -->
      <div class="absolute top-1/3 left-0 right-0 h-px bg-[#2C2C2C] opacity-20"></div>
      <div class="absolute top-1/2 left-0 right-0 h-px bg-[#2C2C2C] opacity-10"></div>
      <div class="absolute bottom-1/3 left-0 right-0 h-px bg-[#2C2C2C] opacity-20"></div>
    </div>

    <!-- Section Label -->
    <div class="absolute top-6 left-6 md:top-8 md:left-8 z-10">
      <p class="text-[10px] text-[#555] uppercase tracking-widest font-mono">
        Section 01 / Login Portal
      </p>
    </div>

    <!-- Measurement Annotation -->
    <div class="absolute top-6 right-6 md:top-8 md:right-8 z-10">
      <p class="text-[10px] text-[#444] font-mono">
        [100vw, 100vh]
      </p>
    </div>

    <!-- Main Content -->
    <div class="relative z-10 min-h-screen flex items-center justify-center px-6 sm:px-8 md:px-20 py-12 sm:py-20">
      <div class="w-full max-w-5xl">
        <!-- Brand Header -->
        <div class="text-center mb-8 sm:mb-12">
          <h1 class="font-serif text-4xl sm:text-5xl md:text-7xl font-bold text-[#EAEAEA] tracking-tight leading-tight mb-4">
            LeibelTheDev
          </h1>
          <p class="text-sm sm:text-base text-[#B0B0B0] tracking-wide uppercase font-mono">
            Client Portal
          </p>
        </div>

        <!-- Cards Container - Side by side on larger screens -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <!-- Info Card -->
          <div class="bg-[#121212] border border-[#2C2C2C] rounded-xl p-6 sm:p-8 md:p-10">
            <div>
              <h2 class="font-serif text-2xl sm:text-3xl md:text-4xl text-[#EAEAEA] mb-4">
                Welcome
              </h2>
              <p class="text-base sm:text-lg text-[#B0B0B0] leading-relaxed mb-4">
                This portal provides secure access to your live project previews and design visions.
              </p>
              <p class="text-sm sm:text-base text-[#888] leading-relaxed">
                Enter your access code to view your projects.
              </p>
            </div>
          </div>

          <!-- Login Form Card -->
          <div class="bg-[#121212] border border-[#2C2C2C] rounded-xl p-6 sm:p-8 md:p-10">
          <!-- Error Message -->
          <div v-if="errorMessage" class="mb-6 p-4 bg-[#3a1d1d] border border-[#FF7F7F] rounded-lg">
            <p class="text-sm text-[#FF7F7F] font-mono">{{ errorMessage }}</p>
          </div>

          <form @submit.prevent="handleLogin" class="space-y-6">
            <!-- Access Code Input -->
            <div>
              <label for="accessCode" class="block text-xs text-[#555] uppercase tracking-widest font-mono mb-2">
                Access Code
              </label>
              <input
                id="accessCode"
                v-model="accessCode"
                type="text"
                required
                :disabled="loading"
                autocomplete="off"
                class="w-full bg-[#111111] border border-[#444] rounded-lg px-6 py-3 text-[#EAEAEA] placeholder-[#666] focus:outline-none focus:border-[#B0B0B0] transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-center text-lg sm:text-xl font-mono tracking-wider"
                placeholder="Enter access code"
              />
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              :disabled="loading || !accessCode.trim()"
              class="w-full bg-white text-black font-medium px-6 py-4 rounded-lg hover:opacity-90 transition duration-300 uppercase tracking-wide text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="!loading">Access Portal</span>
              <span v-else class="font-mono">Verifying...</span>
            </button>
          </form>

          <!-- Additional Info -->
          <div class="mt-6 pt-6 border-t border-[#2C2C2C]">
            <p class="text-xs text-[#666] text-center font-mono">
              Need an access code? Contact your project administrator.
            </p>
          </div>
          </div>
        </div>

        <!-- Demo Projects Section -->
        <div class="mt-8 sm:mt-12">
          <div class="bg-[#121212] border border-[#2C2C2C] rounded-xl p-6 sm:p-8">
            <h3 class="font-serif text-xl sm:text-2xl text-[#EAEAEA] mb-2 text-center">
              Try Demo Projects
            </h3>
            <p class="text-xs sm:text-sm text-[#888] text-center mb-6 font-mono">
              Explore our work with these interactive previews
            </p>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <button
                @click="handleDemoAccess('dashboard')"
                :disabled="loading"
                class="bg-[#1a1a1a] border border-[#2C2C2C] rounded-lg px-4 py-3 text-sm text-[#EAEAEA] hover:border-[#B0B0B0] transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-mono uppercase tracking-wide"
              >
                Data Dashboard
              </button>
              <button
                @click="handleDemoAccess('delivery')"
                :disabled="loading"
                class="bg-[#1a1a1a] border border-[#2C2C2C] rounded-lg px-4 py-3 text-sm text-[#EAEAEA] hover:border-[#B0B0B0] transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-mono uppercase tracking-wide"
              >
                Food Delivery
              </button>
              <button
                @click="handleDemoAccess('management')"
                :disabled="loading"
                class="bg-[#1a1a1a] border border-[#2C2C2C] rounded-lg px-4 py-3 text-sm text-[#EAEAEA] hover:border-[#B0B0B0] transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-mono uppercase tracking-wide"
              >
                Project Management
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-8 text-center">
          <p class="text-xs text-[#555] font-mono">
            LeibelTheDev © 2024
          </p>
        </div>
      </div>
    </div>

    <!-- Architectural Label (Bottom Left) -->
    <div class="absolute bottom-6 left-6 md:bottom-8 md:left-8 z-10">
      <p class="text-[10px] text-[#444] font-mono rotate-[-90deg] origin-left">
        LOGIN PORTAL / ENTRY POINT
      </p>
    </div>
  </div>
</template>

<script>
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore'
import { app } from '../firebase/config'

export default {
  name: 'LoginPage',
  data() {
    return {
      accessCode: '',
      loading: false,
      errorMessage: ''
    }
  },
  mounted() {
    const code = this.$route.query.code
    if (code) {
      this.accessCode = String(code).toUpperCase()
    }
  },
  methods: {
    async handleDemoAccess(accessCode) {
      this.loading = true
      this.errorMessage = ''

      try {
        // Validate access code using Firestore
        const db = getFirestore(app)
        const accessCodesRef = collection(db, 'accessCodes')
        const q = query(accessCodesRef, where('code', '==', accessCode), where('active', '==', true))
        const querySnapshot = await getDocs(q)

        if (!querySnapshot.empty) {
          // Valid access code found
          const accessCodeDoc = querySnapshot.docs[0].data()
          const projectId = accessCodeDoc.projectId
          
          if (!projectId) {
            this.errorMessage = 'Demo project is not available. Please contact support.'
            this.loading = false
            return
          }
          
          // Store access in sessionStorage
          sessionStorage.setItem('accessCode', accessCode)
          sessionStorage.setItem('accessGranted', 'true')
          sessionStorage.setItem('projectId', projectId)
          
          // Redirect to preview page immediately
          this.$router.push(`/mockup/${projectId}`).catch(() => {
            // Ignore navigation duplicate errors
          })
          
        } else {
          // Demo not found
          this.errorMessage = 'Demo project is not available. Please try again later.'
        }
        
      } catch (error) {
        // Handle errors
        console.error('Demo access error:', error)
        
        if (error.code === 'permission-denied') {
          this.errorMessage = 'Permission denied. Please contact support.'
        } else if (error.code === 'unavailable') {
          this.errorMessage = 'Service unavailable. Please check your connection and try again.'
        } else {
          this.errorMessage = 'An error occurred. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },
    async handleLogin() {
      this.loading = true
      this.errorMessage = ''

      try {
        // Trim and normalize the access code
        const code = this.accessCode.trim().toUpperCase()
        
        if (!code) {
          this.errorMessage = 'Please enter an access code.'
          this.loading = false
          return
        }

        // Validate access code using Firestore
        const db = getFirestore(app)
        const accessCodesRef = collection(db, 'accessCodes')
        const q = query(accessCodesRef, where('code', '==', code), where('active', '==', true))
        const querySnapshot = await getDocs(q)

        if (!querySnapshot.empty) {
          // Valid access code found
          const accessCodeDoc = querySnapshot.docs[0].data()
          const projectId = accessCodeDoc.projectId
          
          if (!projectId) {
            this.errorMessage = 'Access code is not linked to a project. Please contact support.'
            this.loading = false
            return
          }
          
          // Store access in sessionStorage
          sessionStorage.setItem('accessCode', code)
          sessionStorage.setItem('accessGranted', 'true')
          sessionStorage.setItem('projectId', projectId)
          
          // Redirect to preview page immediately
          this.$router.push(`/mockup/${projectId}`).catch(() => {
            // Ignore navigation duplicate errors
          })
          
        } else {
          // Invalid access code
          this.errorMessage = 'Invalid access code. Please try again.'
        }
        
      } catch (error) {
        // Handle errors
        console.error('Access code validation error:', error)
        
        if (error.code === 'permission-denied') {
          this.errorMessage = 'Permission denied. Please contact support.'
        } else if (error.code === 'unavailable') {
          this.errorMessage = 'Service unavailable. Please check your connection and try again.'
        } else {
          this.errorMessage = 'An error occurred. Please try again.'
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Additional custom styles if needed */
</style>

